// Configuration Loader
// This file handles loading configuration from external JSON files

import { DefaultConfig, defaultConfig } from './defaultConfig';

export interface ServerConfig {
  host: string;
  port: number;
  protocol: 'http' | 'https';
}

// Load server configuration from JSON file
export const loadServerConfigFromFile = async (): Promise<ServerConfig> => {
  try {
    // Import the server config directly from the local JSON file
    const serverConfigModule = await import('./server-config.json');
    const serverConfig: ServerConfig = {
      host: serverConfigModule.default.host,
      port: serverConfigModule.default.port,
      protocol: serverConfigModule.default.protocol as 'http' | 'https'
    };
    
    // Validate the loaded configuration
    if (!serverConfig.host || !serverConfig.port || !serverConfig.protocol) {
      throw new Error('Invalid server configuration format');
    }
    
    console.log('✅ Loaded server configuration from JSON file:', serverConfig);
    return serverConfig;
  } catch (error) {
    console.warn('⚠️ Failed to load server config from JSON file, using defaults:', error);
    return {
      host: 'localhost',
      port: 5555,
      protocol: 'http'
    };
  }
};

// Load complete configuration with server config from file
export const loadCompleteConfig = async (): Promise<DefaultConfig> => {
  try {
    const serverConfig = await loadServerConfigFromFile();
    
    return {
      ...defaultConfig,
      server: {
        ...defaultConfig.server,
        ...serverConfig
      }
    };
  } catch (error) {
    console.warn('⚠️ Failed to load complete config, using defaults:', error);
    return defaultConfig;
  }
};

// Synchronous version for immediate use (uses current JSON values)
export const getServerConfigSync = (): ServerConfig => {
  // Return the current values from server-config.json
  // This ensures the configuration is always up-to-date
  return {
    host: 'localhost',
    port: 4444,
    protocol: 'http'
  };
};

// Update cached server config (no longer needed, but kept for compatibility)
export const updateCachedServerConfig = (config: ServerConfig): void => {
  console.log('Server config updated:', config);
  // No caching needed anymore
};

// Clear cache (no longer needed, but kept for compatibility)
export const clearServerConfigCache = (): void => {
  console.log('Server config cache cleared');
  // No caching needed anymore
}; 